/**
 * Locale contract for the i18n vertical slice (Phase 0) —
 * docs/NORMAX_PERFORMANCE_I18N_PHASE0_IMPLEMENTATION_PLAN.md §4.
 *
 * Languages, default/fallback, storage key, and Intl mapping are LOCKED by
 * docs/NORMAX_PERFORMANCE_I18N_ARCHITECTURE_PLAN.md §4. This module is pure
 * (no localStorage/navigator access) so the resolution algorithm is testable
 * in isolation — same principle as packages/calculation-engine's pure
 * functions and the existing draft-storage.ts try/catch storage pattern.
 */

export type SupportedLocale = "sr" | "hr" | "en";

export const SUPPORTED_LOCALES: readonly SupportedLocale[] = ["sr", "hr", "en"];

/** sr is both the default (first render, nothing stored/detected) and the
 * i18next fallback (missing key in hr/en falls back to sr). */
export const DEFAULT_LOCALE: SupportedLocale = "sr";
export const FALLBACK_LOCALE: SupportedLocale = "sr";

/** localStorage key — matches the existing repo convention (full package
 * name + colon), see draft-storage.ts:4, NOT the architecture plan's
 * illustrative "normax.locale" (explicitly called out there as non-binding). */
export const LOCALE_STORAGE_KEY = "normax-performance:locale";

/** Internal locale code -> Intl.DateTimeFormat/NumberFormat locale string. */
export const INTL_LOCALE_MAP: Record<SupportedLocale, string> = {
  sr: "sr-Latn-RS",
  hr: "hr-HR",
  en: "en-GB",
};

export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Maps a BCP-47 browser language tag to a SupportedLocale by comparing the
 * primary subtag (before the first "-") case-insensitively, e.g.
 * "sr-Latn-RS" -> "sr", "hr-HR" -> "hr", "en-US"/"en-GB" -> "en".
 * Returns null when the tag doesn't match any supported primary subtag.
 */
function mapBrowserTagToLocale(tag: string): SupportedLocale | null {
  const prefix = tag.split("-")[0]?.toLowerCase();
  if (prefix === "sr" || prefix === "hr" || prefix === "en") return prefix;
  return null;
}

/**
 * Pure, testable resolver — precedence: stored value (if valid) > first
 * matching browser language > FALLBACK_LOCALE. No side effects, no direct
 * localStorage/navigator access (caller reads those defensively, see
 * apps/web/src/i18n/index.ts). An invalid/corrupt storedValue never throws —
 * it's simply treated as "no stored preference" and resolution falls
 * through to browser detection.
 */
export function resolveInitialLocale(input: {
  storedValue: string | null;
  navigatorLanguages: readonly string[];
}): SupportedLocale {
  if (isSupportedLocale(input.storedValue)) return input.storedValue;

  for (const tag of input.navigatorLanguages) {
    const mapped = mapBrowserTagToLocale(tag);
    if (mapped) return mapped;
  }

  return FALLBACK_LOCALE;
}
